/**
 * Case-page workflow mask: investigations form a tree via parentInvestigationId.
 * The mask covers the branch containing the selected node (ancestors ∪ {selected} ∪ descendants).
 */

/** Ordered workflow flags (matches backend LinkedHashMap order). */
export const WORKFLOW_FLAG_ORDER = [
  { flag: 'enableDomainIq', outcomeKind: null },
  { flag: 'enableSecurityTrails', outcomeKind: 'SECURITY_TRAILS' },
  { flag: 'enableCrtSh', outcomeKind: 'CRT_SH' },
  { flag: 'enableUrlScan', outcomeKind: 'URL_SCAN_SCAN' },
  { flag: 'enableUrlScanSearch', outcomeKind: 'URL_SCAN_SEARCH' },
  { flag: 'enablePublicWww', outcomeKind: 'PUBLIC_WWW' },
];

export function outcomeByKind(outcomes, kind) {
  if (!outcomes || !kind) return null;
  return outcomes.find((o) => o.tool === kind) ?? null;
}

export function isDomainIqComplete(flags, outcomes) {
  if (!flags?.enableDomainIq) return true;
  const has = (k) => Boolean(outcomeByKind(outcomes, k));
  if (flags.enableSecurityTrails) return has('SECURITY_TRAILS');
  if (flags.enableCrtSh) return has('CRT_SH');
  if (flags.enableUrlScan) return has('URL_SCAN_SCAN');
  if (flags.enableUrlScanSearch) return has('URL_SCAN_SEARCH');
  if (flags.enablePublicWww) return has('PUBLIC_WWW');
  return true;
}

export function isWorkflowStepComplete(step, flags, outcomes) {
  if (!flags?.[step.flag]) return true;
  if (step.flag === 'enableDomainIq') return isDomainIqComplete(flags, outcomes);
  return Boolean(outcomeByKind(outcomes, step.outcomeKind));
}

/** True while any enabled tool lacks a terminal outcome in /details (e.g. PublicWWW still running). */
export function enabledWorkflowToolsPending(flags, outcomes, investigationStatus) {
  if (!flags || typeof flags !== 'object') return false;
  const s = String(investigationStatus || '').toUpperCase();
  if (s === 'COMPLETED' || s === 'FAILED') return false;
  return WORKFLOW_FLAG_ORDER.some((step) => flags[step.flag] && !isWorkflowStepComplete(step, flags, outcomes));
}

export function isInvestigationTerminalStatus(status) {
  const s = String(status || '').toUpperCase();
  return s === 'COMPLETED' || s === 'FAILED';
}

export function allInvestigationsTerminal(investigations) {
  if (!investigations?.length) return false;
  return investigations.every((inv) => isInvestigationTerminalStatus(inv?.status));
}

export function isInvestigationWorkflowStatusBusy(status) {
  const s = String(status || '').toUpperCase();
  return s === 'PENDING' || s === 'RUNNING';
}

/** @param {Array<{ id: string, parentInvestigationId?: string|null }>} investigations */
export function investigationBranchIdSet(investigations, branchAnchorId) {
  const byId = new Map((investigations || []).filter((i) => i?.id).map((i) => [String(i.id), i]));
  const anchor = branchAnchorId != null ? byId.get(String(branchAnchorId)) : null;
  if (!anchor) return new Set();

  const ids = new Set();
  let cur = anchor;
  while (cur) {
    ids.add(String(cur.id));
    const pid = cur.parentInvestigationId != null ? String(cur.parentInvestigationId) : '';
    cur = pid ? byId.get(pid) : null;
  }

  const stack = [String(anchor.id)];
  while (stack.length) {
    const id = stack.pop();
    for (const inv of investigations) {
      if (!inv?.id) continue;
      const p = inv.parentInvestigationId != null ? String(inv.parentInvestigationId) : '';
      if (p === id) {
        const cid = String(inv.id);
        if (!ids.has(cid)) {
          ids.add(cid);
          stack.push(cid);
        }
      }
    }
  }
  return ids;
}

/** Depth from root (root = 0). */
export function investigationDepthFromRoot(investigations, inv) {
  const byId = new Map((investigations || []).filter((i) => i?.id).map((i) => [String(i.id), i]));
  let d = 0;
  let cur = inv;
  while (cur?.parentInvestigationId) {
    d += 1;
    cur = byId.get(String(cur.parentInvestigationId));
    if (!cur) break;
  }
  return d;
}

/**
 * True if any investigation on the selected branch is PENDING/RUNNING.
 * @param {Array<{ id: string, parentInvestigationId?: string|null, status?: string }>} investigations
 */
export function investigationBranchWorkflowBusy(investigations, branchAnchorId) {
  const idSet = investigationBranchIdSet(investigations, branchAnchorId);
  if (idSet.size === 0) return false;
  return (investigations || []).some((inv) => idSet.has(String(inv.id)) && isInvestigationWorkflowStatusBusy(inv.status));
}

/**
 * Pick which investigation to poll for /details HUD: prefer RUNNING over PENDING, then deepest, then id.
 * @returns {string|null}
 */
export function pickBusyInvestigationIdForMask(investigations, branchAnchorId) {
  const idSet = investigationBranchIdSet(investigations, branchAnchorId);
  if (idSet.size === 0) return null;
  const busy = (investigations || []).filter(
    (inv) => inv?.id && idSet.has(String(inv.id)) && isInvestigationWorkflowStatusBusy(inv.status),
  );
  if (!busy.length) return null;
  const running = busy.filter((inv) => String(inv.status || '').toUpperCase() === 'RUNNING');
  const pool = running.length ? running : busy;
  const scored = pool.map((inv) => ({
    inv,
    depth: investigationDepthFromRoot(investigations, inv),
  }));
  scored.sort((a, b) => {
    if (b.depth !== a.depth) return b.depth - a.depth;
    return String(a.inv.id).localeCompare(String(b.inv.id));
  });
  return scored[0]?.inv?.id ?? null;
}
